<script setup lang="ts">
/**
 * A process as a row of equally wide step cards — „Fällig → Aufträge →
 * Rechnungen → Versand“ — each with a numbered disc, its state, a figure (how
 * many of what) with a caption under it, an optional muted sub-line, and on
 * the ACTIVE step only an action (`#action` slot) that moves the process on.
 *
 * Presentational: the caller derives every state; there is at most one
 * `active` step. Each state is told three ways — colour (a bar on the card's
 * top edge, the disc, the figure), a mark in the disc (tick for done, cross
 * for error, the number otherwise, hollow while out of reach) and a status
 * word above the title — so colour is never the only signal. The list is an
 * `<ol>`, the active step carries `aria-current="step"`.
 *
 * Each card is an `NxKpiCard`. Arrows join the cards in the gaps, so the
 * tracks stay equal. The row measures its own width (container query): when
 * narrow it folds into two columns and the arrows go, very narrow into one.
 *
 * Neighbours: `NxStepper` is a wizard (a `v-model` cursor, numbered markers
 * with labels, no figures), `NxStagePath` a flat chevron strip of pipeline
 * stages on a record. This one is a dashboard of the steps of a run, with a
 * figure per step and the next action on the step that is due.
 */
import { CheckmarkIcon, DismissIcon } from '@blpde/nx-icons';
import { computed } from 'vue';
import { NxKpiCard } from '../KpiCard';

export type NxProcessStepState = 'done' | 'active' | 'future' | 'error';

export interface NxProcessStep {
  key: string | number;
  title: string;
  state: NxProcessStepState;
  /** The figure — a count, already formatted. A dash when missing. */
  value?: string | number;
  /** What the figure counts („Positionen im Lauf“), right under it. */
  caption?: string;
  /** A muted line under the figure: what is still open, a hint. */
  subline?: string;
}

const props = withDefaults(
  defineProps<{
    steps: readonly NxProcessStep[];
    /** Accessible name of the list. */
    ariaLabel?: string;
    /** The status words, by state. */
    stateLabels?: Partial<Record<NxProcessStepState, string>>;
    /** Shown for a step without a figure. */
    emptyValue?: string;
  }>(),
  { ariaLabel: 'Ablauf', stateLabels: () => ({}), emptyValue: '–' }
);

defineSlots<{
  /** The action on the active step (a button); receives the step. */
  action?: (props: { step: NxProcessStep; index: number }) => unknown;
}>();

const DEFAULT_LABELS: Record<NxProcessStepState, string> = {
  done: 'Erledigt',
  active: 'Aktuell',
  future: 'Offen',
  error: 'Fehler',
};
const labels = computed(() => ({ ...DEFAULT_LABELS, ...props.stateLabels }));

function hasValue(step: NxProcessStep): boolean {
  return step.value !== undefined && step.value !== null && step.value !== '';
}
</script>

<template>
  <div class="nx-process-steps" data-testid="nx-process-steps">
    <ol
      class="nx-process-steps__list"
      :style="{ '--process-steps-count': props.steps.length }"
      :aria-label="props.ariaLabel"
    >
      <li
        v-for="(step, index) in props.steps"
        :key="step.key"
        class="nx-process-steps__step"
        :class="`is-${step.state}`"
        :aria-current="step.state === 'active' ? 'step' : undefined"
        :data-state="step.state"
        data-testid="nx-process-step"
      >
        <NxKpiCard class="nx-process-steps__card" :top-text="step.title">
          <template #top-start>
            <span class="nx-process-steps__disc" aria-hidden="true">
              <CheckmarkIcon v-if="step.state === 'done'" filled class="nx-process-steps__mark" />
              <DismissIcon
                v-else-if="step.state === 'error'"
                filled
                class="nx-process-steps__mark"
              />
              <template v-else>{{ index + 1 }}</template>
            </span>
            <span class="nx-process-steps__status">{{ labels[step.state] }}</span>
          </template>

          <span class="nx-process-steps__value">
            {{ hasValue(step) ? step.value : props.emptyValue }}
          </span>
          <span v-if="step.caption" class="nx-process-steps__caption">{{ step.caption }}</span>
          <span v-if="step.subline" class="nx-process-steps__subline">{{ step.subline }}</span>

          <template v-if="step.state === 'active' && $slots.action" #footer>
            <slot name="action" :step="step" :index="index" />
          </template>
        </NxKpiCard>

        <span
          v-if="index < props.steps.length - 1"
          class="nx-process-steps__connector"
          aria-hidden="true"
        />
      </li>
    </ol>
  </div>
</template>

<style scoped>
/* The container the fold query measures. */
.nx-process-steps {
  container-type: inline-size;
}

.nx-process-steps__list {
  display: grid;
  grid-template-columns: repeat(var(--process-steps-count, 4), minmax(0, 1fr));
  gap: var(--nxt-space-md);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: inherit;
}

.nx-process-steps__step {
  --process-step-color: var(--nxt-border-strong);
  --process-step-figure: var(--nxt-fg-muted);
  --process-step-word: var(--nxt-fg-muted);

  position: relative;
  display: grid;
}
.nx-process-steps__step.is-done {
  --process-step-color: var(--nxt-success-bg);
  --process-step-figure: var(--nxt-success-subtle-fg);
  --process-step-word: var(--nxt-success-subtle-fg);
}
.nx-process-steps__step.is-active {
  --process-step-color: var(--nxt-brand-bg);
  --process-step-figure: var(--nxt-brand-subtle-fg);
  --process-step-word: var(--nxt-brand-subtle-fg);
}
.nx-process-steps__step.is-error {
  --process-step-color: var(--nxt-danger-bg);
  --process-step-figure: var(--nxt-danger-subtle-fg);
  --process-step-word: var(--nxt-danger-subtle-fg);
}

/* The card keeps the KPI surface in every state; only the active step gets
   the card's soft corner wash, in brand. The KPI card's leading-edge accent
   gives way to the bar across the top, which reads along the row. */
.nx-process-steps__step .nx-process-steps__card {
  --kpi-card-accent: transparent;
  --kpi-card-figure: var(--process-step-figure);

  justify-content: flex-start;
  box-shadow: var(--nxt-shadow-sm);
}
.nx-process-steps__step.is-active .nx-process-steps__card {
  --kpi-card-accent: var(--nxt-brand-bg);

  border-color: color-mix(in srgb, var(--nxt-brand-bg) 35%, var(--nxt-border));
  box-shadow: var(--nxt-shadow-md);
}
.nx-process-steps__card::after {
  content: '';
  position: absolute;
  inset-block-start: 0;
  inset-inline: 0;
  block-size: calc(var(--nxt-hairline) * 3);
  background: var(--process-step-color);
}

/* Disc across two rows, status word above the title beside it. */
.nx-process-steps__card :deep(.nx-kpi-card__top-row) {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  column-gap: var(--nxt-space-sm);
  align-items: center;
  margin-block-end: var(--nxt-space-xs);
}
.nx-process-steps__card :deep(.nx-kpi-card__top) {
  grid-row: 2;
  grid-column: 2;
  align-self: start;
}
.nx-process-steps__card :deep(.nx-kpi-card__footer) {
  margin-block-start: auto;
  padding-block-start: var(--nxt-space-sm);
}

.nx-process-steps__disc {
  grid-row: 1 / span 2;
  grid-column: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--nxt-tile-size-md);
  block-size: var(--nxt-tile-size-md);
  border: calc(var(--nxt-hairline) * 2) solid var(--nxt-border-strong);
  border-radius: var(--nxt-radius-full);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-md);
  font-weight: var(--nxt-font-weight-bold);
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
.is-done .nx-process-steps__disc,
.is-active .nx-process-steps__disc,
.is-error .nx-process-steps__disc {
  border-color: var(--process-step-color);
  background: var(--process-step-color);
}
.is-done .nx-process-steps__disc {
  color: var(--nxt-success-fg);
}
.is-error .nx-process-steps__disc {
  color: var(--nxt-danger-fg);
}
.is-active .nx-process-steps__disc {
  color: var(--nxt-brand-fg);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 4)
    color-mix(in srgb, var(--nxt-brand-bg) 16%, transparent);
}
.nx-process-steps__mark {
  width: var(--nxt-icon-size-md);
  height: var(--nxt-icon-size-md);
}

.nx-process-steps__status {
  grid-row: 1;
  grid-column: 2;
  align-self: end;
  min-width: 0;
  overflow: hidden;
  color: var(--process-step-word);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nx-process-steps__value {
  display: block;
  font-variant-numeric: tabular-nums;
}
.nx-process-steps__caption,
.nx-process-steps__subline {
  display: block;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-regular);
  letter-spacing: normal;
  line-height: var(--nxt-line-height-normal);
}
.nx-process-steps__subline {
  margin-block-start: var(--nxt-space-2xs);
  color: var(--nxt-fg-subtle);
}

/* Exactly as wide as the gap, so an arrow sits between two cards without
   either making room for it. */
.nx-process-steps__connector {
  position: absolute;
  inset-block-start: 50%;
  inset-inline-start: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--nxt-space-md);
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-sm);
  line-height: 1;
  translate: 0 -50%;
}
/* A text glyph, not an icon: the icon set has no arrow-right, and a chevron
   reads as „aufklappen“ rather than „and then“. */
.nx-process-steps__connector::before {
  content: '→';
}

@container (max-width: 44em) {
  .nx-process-steps__list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  /* Wrapped, an arrow points at the wrong card. */
  .nx-process-steps__connector {
    display: none;
  }
}
@container (max-width: 24em) {
  .nx-process-steps__list {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
